import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { useOrg } from '../contexts/OrgContext';
import BASE_URL from '../config';

/**
 * useCRUDPage - Universal hook for CRUD operations
 * 
 * Manages common state and operations for all CRUD pages:
 * - Data fetching with loading states
 * - Create, Update, Delete operations
 * - Search/filter functionality
 * - Form dialog state management
 * - Delete confirmation modal state
 * - Export to CSV functionality
 * - Permission-based action handling
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.endpoint - API endpoint (e.g., '/api/sites', '/api/vendors')
 * @param {string} config.entityName - Display name for entity (e.g., 'Site', 'Vendor')
 * @param {string} config.entityNamePlural - Plural form (e.g., 'Sites', 'Vendors')
 * @param {Array} config.fields - Form field definitions
 * @param {string} config.idField - The field name used as ID (default: 'id')
 * @param {Function} config.transformCreate - Optional transform function for create payload
 * @param {Function} config.transformUpdate - Optional transform function for update payload
 * @param {Function} config.transformFetch - Optional transform function for fetched data
 * @param {boolean} config.useOrgId - Whether to include organisation_id in requests
 * @param {Function} config.onCreateSuccess - Optional callback after successful create
 * @param {Function} config.onUpdateSuccess - Optional callback after successful update
 * @param {Function} config.onDeleteSuccess - Optional callback after successful delete
 */
export const useCRUDPage = (config) => {
  const {
    endpoint,
    entityName = 'Item',
    entityNamePlural = 'Items',
    fields = [],
    idField = 'id', // Default to 'id', but can be overridden (e.g., 'site_code', 'service_provider_code')
    transformCreate = (data) => data,
    transformUpdate = (data) => data,
    transformFetch = (data) => data,
    useOrgId = true,
    onCreateSuccess,
    onUpdateSuccess,
    onDeleteSuccess,
  } = config;

  const { selectedOrgId, loading: orgLoading } = useOrg();

  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Get authenticated user token
  const getToken = async () => {
    const user = getAuth().currentUser;
    if (!user) throw new Error('User not authenticated');
    return await user.getIdToken();
  };

  // Build URL with optional organisation_id
  const buildUrl = (path, includeOrgId = useOrgId) => {
    const url = `${BASE_URL}${path}`;
    if (includeOrgId && selectedOrgId) {
      const separator = path.includes('?') ? '&' : '?';
      return `${url}${separator}organisation_id=${selectedOrgId}`;
    }
    return url;
  };

  // Fetch data from API
  const fetchData = useCallback(async () => {
    if (useOrgId && (orgLoading || !selectedOrgId)) return;
    
    setLoading(true);
    try {
      const token = await getToken();
      const url = buildUrl(endpoint);
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const fetchedData = transformFetch(response.data || []);
      setData(fetchedData);
    } catch (error) {
      console.error(`Error fetching ${entityNamePlural.toLowerCase()}:`, error);
      toast.error(`Failed to fetch ${entityNamePlural.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [endpoint, selectedOrgId, entityNamePlural, useOrgId, orgLoading]);

  // Fetch data on mount and when selectedOrgId changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create new item
  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  // Edit existing item
  const handleEdit = (item) => {
    setEditing(item);
    setFormOpen(true);
  };

  // Delete item (opens confirmation modal)
  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  // View item (placeholder - can be customized per page)
  const handleView = (item) => {
    console.log('View item:', item);
    // Override this in individual pages if needed
  };

  // Submit form (create or update)
  const handleFormSubmit = async (formData) => {
    try {
      const token = await getToken();

      if (editing) {
        // Update existing item
        const payload = transformUpdate({
          ...formData,
          ...(useOrgId && { organisation_id: selectedOrgId })
        });

        const url = buildUrl(`${endpoint}/${editing[idField]}`);
        
        await axios.put(url, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success(`${entityName} updated successfully`);
        onUpdateSuccess && onUpdateSuccess(payload);
      } else {
        // Create new item
        const payload = transformCreate({
          ...formData,
          ...(useOrgId && { organisation_id: selectedOrgId })
        });

        const url = buildUrl(endpoint);

        await axios.post(url, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success(`${entityName} created successfully`);
        onCreateSuccess && onCreateSuccess(payload);
      }

      setFormOpen(false);
      fetchData();
    } catch (error) {
      console.error(`Error saving ${entityName.toLowerCase()}:`, error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      toast.error(editing ? `Failed to update ${entityName.toLowerCase()}: ${errorMessage}` : `Failed to create ${entityName.toLowerCase()}: ${errorMessage}`);
      throw error;
    }
  };

  // Confirm delete
  const confirmDelete = async () => {
    try {
      const token = await getToken();
      const url = buildUrl(`${endpoint}/${itemToDelete[idField]}`);

      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${entityName} deleted successfully`);
      setDeleteModalOpen(false);
      fetchData();
      onDeleteSuccess && onDeleteSuccess(itemToDelete);
    } catch (error) {
      console.error(`Error deleting ${entityName.toLowerCase()}:`, error);
      // Extract the most specific error message from the response
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      toast.error(errorMessage || `Failed to delete ${entityName.toLowerCase()}`);
      setDeleteModalOpen(false);
    }
  };

  // Export to CSV
  const handleExport = (columns, filteredData = data) => {
    // Extract header names
    const headers = columns
      .filter(col => col.field !== 'actions')
      .map(col => col.headerName);

    // Extract data rows
    const rows = filteredData.map(item =>
      columns
        .filter(col => col.field !== 'actions')
        .map(col => {
          const value = item[col.field];
          return value !== null && value !== undefined ? value : '';
        })
    );

    // Build CSV content
    const csvContent = [
      headers,
      ...rows
    ]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entityNamePlural.toLowerCase()}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Filter data based on search term
  const getFilteredData = (searchFields = []) => {
    if (!searchTerm) return data;

    const search = searchTerm.toLowerCase();
    return data.filter(item =>
      searchFields.some(field => {
        const value = item[field];
        return value && value.toString().toLowerCase().includes(search);
      })
    );
  };

  // Refresh data
  const handleRefresh = () => {
    fetchData();
  };

  return {
    // State
    data,
    loading,
    searchTerm,
    formOpen,
    editing,
    deleteModalOpen,
    itemToDelete,

    // Setters
    setSearchTerm,
    setFormOpen,
    setDeleteModalOpen,

    // Handlers
    handleCreate,
    handleEdit,
    handleDelete,
    handleView,
    handleFormSubmit,
    confirmDelete,
    handleExport,
    handleRefresh,
    getFilteredData,
    
    // Utilities
    fetchData,
  };
};

export default useCRUDPage;
